import * as React from 'react';
import { useSelector } from 'react-redux';

import { getDateRangeValue, getRangeValue } from '../../../packages/JsonQuery';
import { TPopularPlace } from '../../components/PopularPlaces';
import { configSelector } from '../../selectors/configSelector';
import { selectOneTwoTripBannerExperimentEnabled } from '../../selectors/experiments';
import { getDealType } from '../../selectors/getDealType';
import { getJsonQuery } from '../../selectors/getJsonQuery';
import { EDealType } from '../../types/dealType';
import { trackPlaceClick } from './tracking';

const ONE_TWO_TRIP_URL =
  'https://cian.onetwotrip.com/?activePage=hotel&isStart=false&utm_source=cian&utm_medium=partner&utm_campaign=listing_collection&636bd81ac76114bf06b41e60=';

export const usePopularPlaces = () => {
  const isOneTwoTripBannerExperimentEnabled = useSelector(selectOneTwoTripBannerExperimentEnabled);
  const jsonQuery = useSelector(getJsonQuery);
  const config = useSelector(configSelector);
  const dealType = useSelector(getDealType);

  const beds = getRangeValue('beds_count')(jsonQuery);
  const dates = getDateRangeValue('dates')(jsonQuery);

  const onPlaceClick = React.useCallback(
    ({ urlPart, url, analyticsLabel }: TPopularPlace) => {
      trackPlaceClick(analyticsLabel);

      const urlToOpen = url
        ? url
        : `${ONE_TWO_TRIP_URL}${encodeURIComponent(
            getUrlPart({
              urlPart,
              startDate: dates?.gte,
              endDate: dates?.lt,
              guests: beds?.gte || 1,
            }),
          )}`;

      window.open(urlToOpen, '_blank');
    },
    [beds, dates],
  );

  return React.useMemo(() => {
    const places =
      (dealType === EDealType.RentDaily &&
        isOneTwoTripBannerExperimentEnabled &&
        config.get<TPopularPlace[]>('frontendSerp.popularPlaces')) ||
      [];

    return { places, onPlaceClick };
  }, [onPlaceClick, isOneTwoTripBannerExperimentEnabled, config, dealType]);
};

function getUrlPart({
  urlPart,
  startDate,
  endDate,
  guests,
}: {
  urlPart: string;
  startDate?: string;
  endDate?: string;
  guests: number;
}) {
  const startDateQuery = startDate ? `&date_start=${startDate}` : '';
  const endDateQuery = endDate ? `&date_end=${endDate}` : '';
  const guestsQuery = guests ? `&rooms%5B0%5D=${guests}` : '';

  return `${urlPart}${startDateQuery}${endDateQuery}${guestsQuery}`;
}
